import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getFiledId, UserState } from 'src/app/shared/state/user/user.reducer';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastNotificationService } from 'src/app/shared/toast-notification/toast-notification.service';
import { MatDialog } from '@angular/material/dialog';
import saveAs from 'file-saver';
import { BigCommerceService } from '../big-commerce.service';
import { BcModalComponent } from '../bc-modal/bc-modal.component';

@Component({
	selector: 'app-all-products',
	templateUrl: './all-products.component.html',
	styleUrls: ['./all-products.component.scss']
})
export class AllProductsComponent implements OnInit {
	@ViewChild('fileRef') fileRef: ElementRef;
	menuExpanded: boolean;
	private unsubscriber$ = new Subject<void>();
	filedId: number;
	allProducts: any;
	backup: any[];
	loading = false;

	controlA = new FormControl();
	controlB = new FormControl();
	controlC = new FormControl();
	brand: any[];
	category: any[];
	vendor: any[];
	productCategory = null;
	productBrand = null;
	productVendor = null;
	history = false;
	isExpanded: boolean;
	selectedItems: any[];
	optimized: boolean;
	unoptimized: boolean;
	newlyGenerated: boolean;
	scrWidth: number;
	bulkLoading: boolean;
	pageTitle = 'All Products';
	newlyBackup: any[];
	order = '';

	constructor(
		private authenticationService: AuthenticationService,
		private bigCommerceService: BigCommerceService,
		private userStore: Store<UserState>,
		private toastNotificationService: ToastNotificationService,
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private dialog: MatDialog
	) {
		this.userStore.pipe(select(getFiledId), takeUntil(this.unsubscriber$)).subscribe(id => {
			this.filedId = id;
		});
	}

	@HostListener('window:resize', ['$event'])
	getScreenSize(event?): void {
		this.scrWidth = window.innerWidth;
	}

	ngOnInit(): void {
		this.scrWidth = window.innerWidth;
		this.authenticationService.expandMenu$.subscribe(res => {
			this.menuExpanded = res;
		});
		this.getFilterValues();
		this.getActivatedRoute();
	}

	getFlags(): void {
		this.loading = true;
		const payload = {
			user: {
				user_filed_id: this.filedId
			},
			data: {}
		};
		this.bigCommerceService
			.bulkFlagRetrive(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				response => {
					this.loading = response['flags'].generatedFlag;
					this.bigCommerceService.canGenerate$.next(!this.loading);
					if (!this.loading) {
						this.getNewlyGenerated();
						this.resetFlag();
					} else {
						setTimeout(() => {
							this.getFlags();
						}, 20000);
					}
				},
				() => {
					this.loading = false;
				}
			);
	}

	resetFlag(): void {
		const payload = {
			user: {
				user_filed_id: this.filedId
			},
			data: {}
		};
		this.bigCommerceService
			.bulkFlagReset(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				response => {},
				() => {}
			);
	}

	public getActivatedRoute(): void {
		this.activatedRoute.queryParams.pipe(takeUntil(this.unsubscriber$)).subscribe(params => {
			if (params['type'] == 'history') {
				this.history = true;
				this.pageTitle = 'History';
			}
			if (params['isExpanded']) {
				this.isExpanded = true;
				this.pageTitle = 'History';
			}
			if (params['type'] == 'optimized') {
				this.optimized = true;
				this.pageTitle = 'Optimized Products';
			}
			if (params['type'] == 'unoptimized') {
				this.unoptimized = true;
				this.pageTitle = 'Unoptimized Products';
			}
			if (params['type'] == 'newly-generated') {
				this.newlyGenerated = true;
				this.pageTitle = 'Newly Generated';
			}
		});
		if (this.history) {
			this.getProductsHistory();
		} else if (this.newlyGenerated) {
			this.getFlags();
		} else {
			this.getAllProducts();
		}
	}

	getProductsHistory(page = 1): void {
		this.loading = true;
		const payload = {
			user: {
				user_filed_id: this.filedId
			},
			data: {
				page_number: page
			}
		};
		this.bigCommerceService
			.getProductsHistory(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe({
				next: response => {
					this.loading = false;
					this.allProducts = response['data'].history;
					this.allProducts.forEach(item => {
						item['description'] = item.mainDescription;
					});
					this.backup = [...this.allProducts];
				},
				error: () => {
					this.loading = false;
				}
			});
	}

	getNewlyGenerated(page = 1): void {
		this.loading = true;
		const payload = {
			user: {
				user_filed_id: this.filedId
			},
			data: {
				limit: 35,
				page: page
			}
		};
		this.bigCommerceService
			.getNewlyGenerated(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe({
				next: response => {
					this.allProducts = response['data'].generatedData;
					this.allProducts.forEach(item => {
						item['description'] = item['generatedData'].description;
					});
					this.backup = [...this.allProducts];
					this.loading = false;
					this.bulkLoading = false;
				},
				error: () => {
					this.loading = false;
				}
			});
	}

	toEditor(): void {
		this.router.navigate(['big-commerce/generate']);
	}

	sortProducts(value: string, type: string): void {
		this.loading = true;
		const payload = {
			user: {
				user_filed_id: this.filedId
			},
			data: {
				limit: 35,
				page: 1,
				order: this.order
			}
		};

		if (type == 'sort') {
			payload.data['sort'] = value;
		} else {
			this.order = value;
			payload.data['order'] = value;
		}

		if (value == 'relevance') {
			payload.data['sort'] = '';
			payload.data['order'] = '';
		}

		this.bigCommerceService
			.getStoreProducts(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe({
				next: response => {
					this.allProducts = response['data'];
					this.allProducts.forEach(item => {
						item.optimizationScore = Math.round(item.optimizationScore);
					});
					this.backup = [...this.allProducts];
					if (this.optimized) {
						this.allProducts = this.allProducts.filter(item => {
							return item.optimizationScore > 0;
						});
					}
					if (this.unoptimized) {
						this.allProducts = this.allProducts.filter(item => {
							return item.optimizationScore == 0;
						});
					}
					this.loading = false;
				},
				error: () => {
					this.loading = false;
				}
			});
	}

	getFilterValues(): void {
		let payload = {
			user: {
				user_filed_id: this.filedId
			},
			data: {}
		};
		this.bigCommerceService
			.getFilterValues(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe({
				next: response => {
					this.brand = response['data'].brand;
					this.category = response['data'].category;
					this.vendor = this.mapArrayValues(response['data'].productVendors);
				},
				error: () => {
					this.loading = false;
				}
			});
	}

	bulkDownload(): void {
		let payload = {
			user: {
				user_filed_id: this.filedId
			},
			data: {}
		};
		this.bigCommerceService
			.downloadBulk(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				response => {
					this.downloadFile(response['data']);
				},
				() => {
					this.loading = false;
				},
				() => {}
			);
	}

	downloadFile(data: any) {
		const replacer = (key, value) => (value === null ? '' : value); // specify how you want to handle null values here
		const header = Object.keys(data[0]);
		let csv = data.map(row => header.map(fieldName => JSON.stringify(row[fieldName], replacer)).join(','));
		csv.unshift(header.join(','));
		let csvArray = csv.join('\r\n');

		var blob = new Blob([csvArray], { type: 'text/csv' });
		saveAs(blob, 'shakespeare.csv');
	}

	uploadFile(event: Event): void {
		const element = event.currentTarget as HTMLInputElement;
		let fileList: FileList | null = element.files;
		if (fileList && fileList[0].type == 'text/csv') {
			this.bulkUpload(fileList[0]);
		}
	}

	triggerFileUpload(): void {
		this.fileRef.nativeElement.click();
	}

	bulkUpload(file: File): void {
		let payload = {
			user: {
				user_filed_id: this.filedId
			},
			data: {}
		};
		let formData = new FormData();
		formData.append('data', JSON.stringify(payload));
		formData.append('file', file);
		// payload.append('user', )
		let ids = [];
		this.selectedItems?.forEach(item => {
			ids.push(item?.id);
		});
		this.bigCommerceService
			.uploadBulk(formData)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				response => {
					if (response['data'] == 'file uploaded successfully') {
						this.toastNotificationService.sendSuccessToast('File uploaded!');
						this.getNewlyGenerated();
					} else {
						this.toastNotificationService.sendErrorToast('Format not supported');
					}
				},
				() => {
					this.loading = false;
				},
				() => {}
			);
	}

	handleTags(item: object): void {
		console.log(item);
	}

	mapFilterValues(arr: any[]): any[] {
		let newArr = arr.map(item => {
			return { name: item.title, ...item };
		});
		return newArr;
	}

	mapArrayValues(arr: any[]): any[] {
		let newArr = arr.map(item => {
			return { name: item };
		});
		return newArr;
	}

	handlePagination(page: number): void {
		this.getAllProducts(page);
	}

	goBack(): void {
		this.router.navigate(['big-commerce']);
	}

	getAllProducts(page = 1): void {
		if (this.history) {
			this.getProductsHistory(page);
			return;
		}
		if (this.newlyGenerated) {
			this.getNewlyGenerated(page);
			return;
		}
		this.loading = true;
		const payload = {
			user: {
				user_filed_id: this.filedId
			},
			data: {
				limit: 35,
				page: page
			}
		};
		this.bigCommerceService
			.getStoreProducts(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe({
				next: response => {
					this.allProducts = response['data'];
					this.allProducts.forEach(item => {
						item.optimizationScore = Math.round(item.optimizationScore);
					});
					this.backup = [...this.allProducts];
					this.loading = false;
					if (this.optimized) {
						this.allProducts = this.allProducts.filter(item => {
							return item.optimizationScore > 0;
						});
					}
					if (this.unoptimized) {
						this.allProducts = this.allProducts.filter(item => {
							return item.optimizationScore == 0;
						});
					}
				},
				error: () => {
					this.loading = false;
				}
			});
	}

	confirmBulkProcess(payloadData: any[]): void {
		this.selectedItems = payloadData;
		let allowed = {
			title: 'Are you sure?',
			desc: `This process will take 15 to 20 minutes and ${this.selectedItems.length * 70} credits.<br>Do you want to continue?`,
			isCancel: true,
			img: 'assets/icons/big-i.svg',
			cta: 'Continue'
		};
		let notAllowed = {
			title: 'Please wait',
			desc: 'Shakespeare is generating content for you wait till previous task is done',
			isCancel: false,
			img: 'assets/icons/big-i.svg',
			cta: 'Okay'
		};
		let isAllowed = this.bigCommerceService.canGenerate$.value;
		let modalConfig;
		let isMobile = this.scrWidth < 780 ? true : false;
		if (isMobile) {
			modalConfig = {
				width: '90%',
				height: '328px',
				maxWidth: '100%',
				hasBackdrop: false
			};
		} else {
			modalConfig = {
				width: '436px',
				height: '328px',
				hasBackdrop: true
			};
		}
		const dialogRef = this.dialog.open(BcModalComponent, {
			...modalConfig,
			panelClass: 'modal-wrapper',
			disableClose: true,
			data: isAllowed ? allowed : notAllowed
		});
		dialogRef.afterClosed().subscribe(res => {
			if (res.confirm && isAllowed) {
				this.generateBulk();
			}
		});
	}

	generateBulk(): void {
		this.loading = true;
		this.bulkLoading = true;
		const payload = {
			user: {
				user_filed_id: this.filedId
			},
			data: this.selectedItems
		};
		this.bigCommerceService
			.generateBulk(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				response => {
					this.router.navigate(['big-commerce/all-products'], { queryParams: { type: 'newly-generated' } });
					if (response['debugMessage'] == 'Not enough credit') {
						this.toastNotificationService.sendInfoToast("You don't have enough credit");
					} else {
						this.toastNotificationService.sendSuccessToast('Process started...');
					}
					this.getFlags();
					this.bulkLoading = true;
				},
				() => {
					this.loading = false;
					this.toastNotificationService.sendErrorToast('Something went wrong');
				}
			);
	}

	takeTour(): void {
		this.loading = false;
		this.bulkLoading = false;
		this.router.navigate(['big-commerce']);
	}

	clearFilters(): void {
		this.controlA.setValue([]);
		this.controlB.setValue([]);
		this.controlC.setValue([]);
		this.productCategory = null;
		this.productBrand = null;
		this.productVendor = null;
		this.getAllProducts();
	}

	getFilteredProducts(type: string, value: string, page = 1): void {
		if (value == undefined) {
			value = '_cleared_';
		}
		switch (type) {
			case 'brand':
				this.productBrand = value['id'];
				break;
			case 'product_vendor':
				this.productVendor = value['name'];
				break;
			case 'category':
				this.productCategory = value['id'];
				break;
		}
		this.loading = true;
		const payload = {
			user: {
				user_filed_id: this.filedId
			},
			data: {
				limit: 35,
				page: page,
				sort: '',
				order: ''
			}
		};
		this.productBrand !== '_cleared_' && this.productBrand !== null && (payload.data['brand_id'] = this.productBrand);
		this.productVendor !== '_cleared_' && this.productVendor !== null && (payload.data['availability'] = this.productVendor);
		this.productCategory !== '_cleared_' && this.productCategory !== null && (payload.data['category_id'] = this.productCategory);

		this.bigCommerceService
			.getStoreProducts(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe({
				next: response => {
					this.allProducts = response['data'];
					this.allProducts.forEach(item => {
						item.optimizationScore = Math.round(item.optimizationScore);
					});
					this.backup = [...this.allProducts];
					if (this.optimized) {
						this.allProducts = this.allProducts.filter(item => {
							return item.optimizationScore > 0;
						});
					}
					if (this.unoptimized) {
						this.allProducts = this.allProducts.filter(item => {
							return item.optimizationScore == 0;
						});
					}
					this.loading = false;
				},
				error: () => {
					this.loading = false;
				}
			});
	}

	public debounce(callback, delay: number = 1000) {
		let timeout;
		return (...args) => {
			clearTimeout(timeout);
			timeout = setTimeout(() => {
				// callback(...args);
			}, delay);
		};
	}

	searchTemplate(searchQuery: string): void {
		if (this.newlyGenerated) {
			this.allProducts = [...this.backup];
			this.allProducts = this.allProducts.filter(item => {
				return (
					item.generatedData.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
					item.generatedData.description.toLowerCase().includes(searchQuery.toLowerCase())
				);
			});
		} else if (this.history) {
			this.allProducts = [...this.backup];
			this.allProducts = this.allProducts.filter(item => {
				return item.title.toLowerCase().includes(searchQuery.toLowerCase()) || item.mainDescription.toLowerCase().includes(searchQuery.toLowerCase());
			});
		} else {
			this.allProducts = [...this.backup];
			this.allProducts = this.allProducts.filter(item => {
				return item.title.toLowerCase().includes(searchQuery.toLowerCase()) || item.description.toLowerCase().includes(searchQuery.toLowerCase());
			});
		}
	}

	searchApiCall(value: string): void {
		if (value == '') {
			this.allProducts = [...this.backup];
			return;
		}
		const payload = {
			user: {
				user_filed_id: this.filedId
			},
			data: {
				limit: 1,
				search: value
			}
		};
		this.bigCommerceService
			.seacrchProducts(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				response => {
					this.allProducts = response['data'];
					this.allProducts.forEach(item => {
						item.optimizationScore = Math.round(item.optimizationScore);
					});
					this.loading = false;
				},
				() => {
					this.loading = false;
				},
				() => {}
			);
	}
}
